import { motion } from 'framer-motion'
import { Link, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { springLayout } from '../../design/motion'
import { NavDot, type DotKind } from './NavDot'
import { activeItem, type DotKey, type NavItem } from './navGroups'

interface Props {
  items: NavItem[]
  /** Which destinations are carrying a dot, and what each one means. */
  dots?: Partial<Record<DotKey, DotKind | false>>
}

/**
 * The destinations inside the current bottom tab, as underlined tabs.
 *
 * Equal widths and a gym-colour underline, the most familiar tab shape there
 * is. They used to be pills: a white pill on a near-white header read as a
 * pressed button, not as "you are here".
 *
 * Choices made inside a page (News' feeds, the standings filters) are chips
 * (`design/ChipTabs`), never a second underlined strip, so the two levels never
 * look alike.
 *
 * Each tab grows to an equal share but never below its label, so a gym with
 * four destinations scrolls rather than squeezing a word onto two lines.
 *
 * Selection is not `NavLink`'s: a tab that holds nested destinations has to
 * stay lit while you are on one of them, and `NavLink` only knows its own path.
 */
export function SectionTabs({ items, dots }: Props) {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  // A single destination is not a choice, so it needs no chrome.
  if (items.length < 2) return null

  const current = activeItem(items, pathname)

  return (
    <nav className="scrollbar-hide overflow-x-auto px-2">
      <ul className="flex">
        {items.map((item) => {
          const isActive = current?.to === item.to
          const dot = item.dot ? (dots?.[item.dot] ?? false) : false
          return (
            <li key={item.to} className="min-w-fit flex-1 basis-0">
              <Link
                to={item.to}
                aria-current={isActive ? 'page' : undefined}
                className="relative flex items-center justify-center gap-1.5 px-3 pb-3 pt-2.5 text-[14px]"
              >
                <span
                  className={`whitespace-nowrap transition-colors ${
                    isActive ? 'font-semibold text-foreground' : 'font-medium text-foreground/50'
                  }`}
                >
                  {t(item.labelKey)}
                </span>
                {dot && <NavDot kind={dot} />}
                {isActive && (
                  <motion.span
                    layoutId="section-tab-indicator"
                    transition={springLayout}
                    aria-hidden
                    className="absolute inset-x-4 bottom-0 h-[3px] rounded-t-full bg-[var(--gym-primary)]"
                  />
                )}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
